export type ApifyPresetId = 'google_maps' | 'google_search';
export type ScrapeJobSource = 'urls' | 'apify' | 'google_places' | 'google_serp';

export type ScrapeFetchEngineId = 'fetch' | 'playwright';

export type ScrapeJobFormState = {
  jobSource: ScrapeJobSource;
  fetchEngine: ScrapeFetchEngineId;
  seedUrls: string;
  apifyPreset: ApifyPresetId;
  apifyNiche: string;
  apifyRegion: string;
  apifyQueries: string;
  apifyMaxItems: number;
  websiteEnrichEnabled: boolean;
};

export type BuiltScrapeJobConfig =
  | { source: 'urls'; seed_urls: string[]; engine: ScrapeFetchEngineId }
  | {
      source: 'apify';
      seed_urls: string[];
      apify_preset: ApifyPresetId;
      segmentation: {
        niche: string | null;
        region: string | null;
        queries?: string[];
      };
      apify: { max_items: number };
      website_enrich?: { enabled: boolean };
    }
  | {
      source: 'google_places';
      seed_urls: string[];
      segmentation: {
        niche: string | null;
        region: string | null;
        queries?: string[];
      };
      google_places: { max_items: number; language_code: string; region_code: string };
      website_enrich?: { enabled: boolean };
    }
  | {
      source: 'google_serp';
      seed_urls: string[];
      segmentation: {
        niche: string | null;
        region: string | null;
        queries?: string[];
      };
      google_serp: { max_items: number; gl: string; lr: string; safe: 'active' | 'off' };
      website_enrich?: { enabled: boolean };
    };

export function initialScrapeJobFormState(
  overrides?: Partial<ScrapeJobFormState>,
): ScrapeJobFormState {
  return {
    jobSource: 'google_places',
    fetchEngine: 'fetch',
    seedUrls: '',
    apifyPreset: 'google_maps',
    apifyNiche: '',
    apifyRegion: '',
    apifyQueries: '',
    apifyMaxItems: 200,
    websiteEnrichEnabled: true,
    ...overrides,
  };
}

function segmentationValid(state: ScrapeJobFormState): boolean {
  const queries = state.apifyQueries
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const niche = state.apifyNiche.trim();
  const region = state.apifyRegion.trim();
  return queries.length > 0 || Boolean(niche) || Boolean(region);
}

export function isScrapeJobFormValid(state: ScrapeJobFormState): boolean {
  if (state.jobSource === 'urls') {
    return state.seedUrls
      .split('\n')
      .some((l) => l.trim());
  }
  return segmentationValid(state);
}

export function buildScrapeJobConfig(state: ScrapeJobFormState): BuiltScrapeJobConfig {
  if (state.jobSource === 'urls') {
    const urls = state.seedUrls
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    return { source: 'urls', seed_urls: urls, engine: state.fetchEngine };
  }
  const queries = state.apifyQueries
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const segmentation = {
    niche: state.apifyNiche.trim() || null,
    region: state.apifyRegion.trim() || null,
    ...(queries.length ? { queries } : {}),
  };
  const website_enrich = { enabled: state.websiteEnrichEnabled };

  if (state.jobSource === 'google_places') {
    return {
      source: 'google_places',
      seed_urls: [],
      segmentation,
      google_places: {
        max_items: state.apifyMaxItems,
        language_code: 'nl',
        region_code: 'NL',
      },
      website_enrich,
    };
  }

  if (state.jobSource === 'google_serp') {
    return {
      source: 'google_serp',
      seed_urls: [],
      segmentation,
      google_serp: {
        max_items: Math.min(state.apifyMaxItems, 300),
        gl: 'nl',
        lr: 'lang_nl',
        safe: 'active',
      },
      website_enrich,
    };
  }

  return {
    source: 'apify',
    seed_urls: [],
    apify_preset: state.apifyPreset,
    segmentation,
    apify: { max_items: state.apifyMaxItems },
    website_enrich,
  };
}

export type ScraperCapabilities = {
  google_places?: {
    configured: boolean;
    label: string;
    recommended?: boolean;
    docs_hint?: string;
  };
  google_serp?: {
    configured: boolean;
    label: string;
    recommended?: boolean;
    docs_hint?: string;
  };
  apify: {
    configured: boolean;
    presets: { id: ApifyPresetId; label: string; default_actor_id: string }[];
  };
  fetch: {
    playwright_enabled: boolean;
    engines: { id: ScrapeFetchEngineId; label: string }[];
  };
};
